import React, { useEffect } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { AdminRoute, ErrorBoundary, ProtectedRoute } from '../components/common';
import { Dashboard } from '../components/Dashboard';
import { Navbar } from '../components/Navbar';
import { WelcomeScreenModal } from '../components/identity/screens/WelcomeScreen';
import type { IdentityStep } from '../components/identity/types';
import { useAppContext } from '../contexts/useAppContext';
import { useJWTAuth } from '../contexts/useJWTAuth';

import { AdminMtDetailPage } from '../pages/admin/MtDetail';
import { AdminScansPage } from '../pages/admin/Dashboard';
import { AdminKnowledgeUpdatesPage } from '../pages/admin/KnowledgeUpdates';
import { AnalysisJobPage } from '../pages/AnalysisJobPage';
import { AnalysisResultsPage } from '../pages/AnalysisResultsPage';
import { AutarPlatformEarlyConceptPage } from '../pages/AutarPlatformEarlyConceptPage';
import { AutarPlatformV2DescriptionsPage } from '../pages/AutarPlatformV2DescriptionsPage';
import { AutarPlatformV2DiagramPage } from '../pages/AutarPlatformV2DiagramPage';
import CliAuthPage from '../pages/CliAuthPage';
import { DemoAnalysisPage } from '../pages/DemoAnalysisPage';
import { OrganizationsPage } from '../pages/OrganizationsPage';
import { OrganizationDetailPage } from '../pages/OrganizationDetailPage';
import { FleetPage } from '../pages/FleetPage';
import { FleetReportPage } from '../pages/FleetReportPage';
import { PracticeReposPage } from '../pages/PracticeReposPage';
import { CampaignPage } from '../pages/CampaignPage';
import { DocumentViewPage } from '../pages/DocumentViewPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import { HistoryPage } from '../pages/HistoryPage';
import InvitationAcceptPage from '../pages/InvitationAcceptPage';
import { KnowledgeGraphPage } from '../pages/KnowledgeGraphPage';
import OAuthCallbackPage from '../pages/OAuthCallbackPage';
import OAuthErrorPage from '../pages/OAuthErrorPage';
import OAuthSettingsPage from '../pages/OAuthSettingsPage';
import { ProjectConceptsPage } from '../pages/ProjectConceptsPage';
import { QuickScanPage } from '../pages/QuickScanPage';
import { RepositoryDetailPage } from '../pages/RepositoryDetailPage';
import { RepositorySelectionPage } from '../pages/RepositorySelectionPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import SignInPage from '../pages/SignInPage';
import SignUpPage from '../pages/SignUpPage';
import { TaskExecutionPage } from '../pages/TaskExecutionPage';
import TeamSettingsPage from '../pages/TeamSettingsPage';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import { VirtualEnvironmentSetupPage } from '../pages/VirtualEnvironmentSetupPage';
import { ZipUploadPage } from '../pages/ZipUploadPage';

const WelcomePageWrapper: React.FC = () => {
  const { setDemoMode, setSelectedRepository } = useAppContext();
  const { isAuthenticated, isLoading: authLoading } = useJWTAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleStepChange = (step: IdentityStep) => {
    if (step === 'demo') {
      setDemoMode(true);
      const demoRepository = {
        id: 'demo-project',
        name: 'demo-project',
        description: 'Sample project for demonstration',
        updatedAt: new Date().toISOString(),
        language: 'TypeScript',
        private: true,
        isPrivate: true,
        stars: 0,
        forks: 0,
        url: 'https://github.com/demo/demo-project',
        fullName: 'demo/demo-project',
        size: 1024,
        lastUpdated: new Date(),
        source: 'git' as const,
      };
      setSelectedRepository(demoRepository);
      navigate('/setup');
    }
  };

  if (!authLoading && isAuthenticated) {
    return null;
  }

  return <WelcomeScreenModal onStepChange={handleStepChange} />;
};

const DashboardWrapper: React.FC = () => {
  const { user } = useJWTAuth();
  const location = useLocation();
  const isFirstVisit = location.state?.isFirstVisit || false;
  return <Dashboard user={user} isFirstVisit={isFirstVisit} />;
};

const NotFoundPage: React.FC = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1 flex items-center justify-center px-6 pt-24">
      <div className="text-center">
        <h1 className="text-6xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          404
        </h1>
        <p className="text-lg mb-6" style={{ color: 'var(--color-text-secondary)' }}>
          Page not found
        </p>
        <Link
          to="/dashboard"
          className="inline-block px-6 py-2 rounded-lg font-medium text-white transition-colors"
          style={{ backgroundColor: 'var(--color-accent-teal)' }}
        >
          Go to Dashboard
        </Link>
      </div>
    </main>
  </div>
);

const withErrorBoundary = (element: React.ReactNode) => <ErrorBoundary>{element}</ErrorBoundary>;

export const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={withErrorBoundary(<WelcomePageWrapper />)} />
    <Route path="/signin" element={withErrorBoundary(<SignInPage />)} />
    <Route path="/signup" element={withErrorBoundary(<SignUpPage />)} />
    <Route path="/auth/callback" element={withErrorBoundary(<OAuthCallbackPage />)} />
    <Route
      path="/auth/cli"
      element={<ProtectedRoute>{withErrorBoundary(<CliAuthPage />)}</ProtectedRoute>}
    />
    <Route path="/oauth/error" element={withErrorBoundary(<OAuthErrorPage />)} />
    <Route path="/verify-email" element={withErrorBoundary(<VerifyEmailPage />)} />
    <Route path="/verify-email/:token" element={withErrorBoundary(<VerifyEmailPage />)} />
    <Route path="/forgot-password" element={withErrorBoundary(<ForgotPasswordPage />)} />
    <Route path="/reset-password/:token" element={withErrorBoundary(<ResetPasswordPage />)} />
    <Route path="/invitations/:token" element={withErrorBoundary(<InvitationAcceptPage />)} />

    <Route
      path="/AutarPlatform"
      element={
        <ProtectedRoute>{withErrorBoundary(<AutarPlatformEarlyConceptPage />)}</ProtectedRoute>
      }
    />
    <Route
      path="/brand"
      element={<ProtectedRoute>{withErrorBoundary(<ProjectConceptsPage />)}</ProtectedRoute>}
    />
    <Route
      path="/AutarPlatformV2Diagram"
      element={<ProtectedRoute>{withErrorBoundary(<AutarPlatformV2DiagramPage />)}</ProtectedRoute>}
    />
    <Route
      path="/AutarPlatformV2Descriptions"
      element={
        <ProtectedRoute>{withErrorBoundary(<AutarPlatformV2DescriptionsPage />)}</ProtectedRoute>
      }
    />
    <Route
      path="/settings"
      element={<ProtectedRoute>{withErrorBoundary(<OAuthSettingsPage />)}</ProtectedRoute>}
    />
    <Route
      path="/settings/team"
      element={<ProtectedRoute>{withErrorBoundary(<TeamSettingsPage />)}</ProtectedRoute>}
    />
    <Route
      path="/dashboard"
      element={<ProtectedRoute>{withErrorBoundary(<DashboardWrapper />)}</ProtectedRoute>}
    />
    <Route
      path="/repositories"
      element={<ProtectedRoute>{withErrorBoundary(<RepositorySelectionPage />)}</ProtectedRoute>}
    />
    <Route
      path="/orgs"
      element={<ProtectedRoute>{withErrorBoundary(<OrganizationsPage />)}</ProtectedRoute>}
    />
    <Route
      path="/orgs/:id"
      element={<ProtectedRoute>{withErrorBoundary(<OrganizationDetailPage />)}</ProtectedRoute>}
    />
    <Route
      path="/orgs/:id/fleet"
      element={<ProtectedRoute>{withErrorBoundary(<FleetPage />)}</ProtectedRoute>}
    />
    <Route
      path="/orgs/:id/insights"
      element={<ProtectedRoute>{withErrorBoundary(<FleetReportPage />)}</ProtectedRoute>}
    />
    <Route
      path="/orgs/:id/practice/:slug"
      element={<ProtectedRoute>{withErrorBoundary(<PracticeReposPage />)}</ProtectedRoute>}
    />
    <Route
      path="/campaigns/:id"
      element={<ProtectedRoute>{withErrorBoundary(<CampaignPage />)}</ProtectedRoute>}
    />
    <Route
      path="/repository/:id"
      element={<ProtectedRoute>{withErrorBoundary(<RepositoryDetailPage />)}</ProtectedRoute>}
    />
    <Route
      path="/history"
      element={<ProtectedRoute>{withErrorBoundary(<HistoryPage />)}</ProtectedRoute>}
    />
    <Route
      path="/upload"
      element={<ProtectedRoute>{withErrorBoundary(<ZipUploadPage />)}</ProtectedRoute>}
    />
    <Route
      path="/scan"
      element={<ProtectedRoute>{withErrorBoundary(<QuickScanPage />)}</ProtectedRoute>}
    />
    <Route
      path="/jobs/:jobId/kg"
      element={<ProtectedRoute>{withErrorBoundary(<KnowledgeGraphPage />)}</ProtectedRoute>}
    />
    <Route
      path="/jobs/:jobId/results"
      element={<ProtectedRoute>{withErrorBoundary(<AnalysisResultsPage />)}</ProtectedRoute>}
    />
    <Route
      path="/jobs/:jobId"
      element={<ProtectedRoute>{withErrorBoundary(<AnalysisJobPage />)}</ProtectedRoute>}
    />

    <Route
      path="/admin/scans"
      element={<AdminRoute>{withErrorBoundary(<AdminScansPage />)}</AdminRoute>}
    />
    <Route
      path="/admin/task-executions"
      element={<AdminRoute>{withErrorBoundary(<AdminScansPage />)}</AdminRoute>}
    />
    <Route
      path="/admin/maintenance-tasks"
      element={<AdminRoute>{withErrorBoundary(<AdminScansPage />)}</AdminRoute>}
    />
    <Route
      path="/admin/maintenance-tasks/:taskClass"
      element={<AdminRoute>{withErrorBoundary(<AdminMtDetailPage />)}</AdminRoute>}
    />
    <Route
      path="/admin/knowledge-updates"
      element={<AdminRoute>{withErrorBoundary(<AdminKnowledgeUpdatesPage />)}</AdminRoute>}
    />

    <Route
      path="/tasks/:taskId"
      element={<ProtectedRoute>{withErrorBoundary(<TaskExecutionPage />)}</ProtectedRoute>}
    />
    <Route path="/setup" element={withErrorBoundary(<VirtualEnvironmentSetupPage />)} />
    <Route path="/analysis/:jobId" element={withErrorBoundary(<DemoAnalysisPage />)} />
    <Route
      path="/repository/:repoId/analysis/:jobId/docs/:docIndex"
      element={withErrorBoundary(<DocumentViewPage />)}
    />
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);
